// src/pages/admin/ReviewsPage.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, deleteDoc, addDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { Loader, AlertCircle, Trash, Star, Plus } from 'lucide-react';

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState({ show: false, message: '', type: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [newReview, setNewReview] = useState({
    fullName: '',
    content: '',
    subjectId: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // טעינת תחומים
      const subjectsSnapshot = await getDocs(collection(db, 'subjects'));
      const subjectsData = subjectsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSubjects(subjectsData);

      // טעינת המלצות
      const reviewsSnapshot = await getDocs(collection(db, 'reviews'));
      const reviewsData = reviewsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setReviews(reviewsData);
    } catch (err) {
      setError('אירעה שגיאה בטעינת הנתונים');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getSubjectTitle = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject?.name || 'לא מוגדר';
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק המלצה זו?')) return;

    try {
      await deleteDoc(doc(db, 'reviews', reviewId));
      setReviews(reviews.filter(review => review.id !== reviewId));
      setSaveStatus({ show: true, message: 'ההמלצה נמחקה בהצלחה', type: 'success' });
    } catch (err) {
      setSaveStatus({ show: true, message: 'שגיאה במחיקת ההמלצה', type: 'error' });
      console.error(err);
    }
    setTimeout(() => setSaveStatus({ show: false }), 3000);
  };

  const handleAddReview = async (e) => {
    e.preventDefault();
    if (!newReview.fullName || !newReview.content || !newReview.subjectId) {
      setSaveStatus({ show: true, message: 'נא למלא את כל השדות', type: 'error' });
      return;
    }

    try {
      const docRef = await addDoc(collection(db, 'reviews'), newReview);
      setReviews([...reviews, { id: docRef.id, ...newReview }]);
      setNewReview({ fullName: '', content: '', subjectId: '' });
      setShowAddForm(false);
      setSaveStatus({ show: true, message: 'ההמלצה נוספה בהצלחה', type: 'success' });
    } catch (err) {
      setSaveStatus({ show: true, message: 'שגיאה בהוספת ההמלצה', type: 'error' });
      console.error(err);
    }
    setTimeout(() => setSaveStatus({ show: false }), 3000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-red-500">
        <AlertCircle className="w-6 h-6 mr-2" />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl md:text-2xl font-bold">ניהול המלצות</h1>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-2 hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>הוסף המלצה</span>
        </button>
      </div>

      {saveStatus.show && (
        <div className={`
          mb-6 px-4 py-2 rounded-lg text-sm md:text-base
          ${saveStatus.type === 'success' ? 'bg-green-100 text-green-700' : ''}
          ${saveStatus.type === 'error' ? 'bg-red-100 text-red-700' : ''}
        `}>
          {saveStatus.message}
        </div>
      )}

      {/* טופס הוספת המלצה */}
      {showAddForm && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <form onSubmit={handleAddReview} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                שם מלא
              </label>
              <input
                type="text"
                value={newReview.fullName}
                onChange={(e) => setNewReview({ ...newReview, fullName: e.target.value })}
                className="w-full p-2 border rounded-md"
                placeholder="הכנס שם מלא"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                תחום
              </label>
              <select
                value={newReview.subjectId}
                onChange={(e) => setNewReview({ ...newReview, subjectId: e.target.value })}
                className="w-full p-2 border rounded-md"
              >
                <option value="">בחר תחום</option>
                {subjects.map(subject => (
                  <option key={subject.id} value={subject.id}>
                    {subject.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                תוכן ההמלצה
              </label>
              <textarea
                value={newReview.content}
                onChange={(e) => setNewReview({ ...newReview, content: e.target.value })}
                className="w-full p-2 border rounded-md"
                rows="4"
                placeholder="הכנס את תוכן ההמלצה"
              />
            </div>

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-50"
              >
                ביטול
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                הוסף המלצה
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid gap-6">
        {reviews.map(review => (
          <div key={review.id} className="bg-white p-6 rounded-lg shadow-md">
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-lg">{review.fullName}</h3>
                  <p className="text-sm text-gray-600">
                    תחום: {getSubjectTitle(review.subjectId)}
                  </p>
                </div>
                <button
                  onClick={() => handleDelete(review.id)}
                  className="text-red-600 hover:text-red-700 flex items-center gap-1 p-1"
                >
                  <Trash className="w-4 h-4" />
                  <span className="text-sm">מחק</span>
                </button>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg relative">
                <Star className="absolute top-2 right-2 w-4 h-4 text-yellow-400" />
                <p className="text-gray-700">{review.content}</p>
              </div>
            </div>
          </div>
        ))}

        {reviews.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg shadow-md">
            <p className="text-gray-500">אין המלצות להצגה</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewsPage;